import { Button, Col, Modal, Row } from "react-bootstrap";
import useStore from "../hooks/useStore";
import { useEffect, useRef, useState } from "react";
import { Formik, Field, Form, ErrorMessage } from "formik";
import { clearHubSpotState } from "../utils/hubspot";
import * as Yup from "yup";
import { toast } from "react-toastify";
import useFlow from "../hooks/useFlow";
import { useNavigate } from "react-router-dom";
import { getAnalytics, logEvent } from "firebase/analytics";
import * as Sentry from "@sentry/react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import {
  itemCheckoutAmount,
  itemDisplayName,
  lineTaxValue,
} from "../utils/storeItem";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

// Track 1 magnetic stripe: %B<PAN>^<Name>^<YYMM><ServiceCode(3)><DiscretionaryData>
// Groups: (1) PAN, (2) YY, (3) MM
const TRACK1_REGEX = /%B(\d{13,19})\^[^^]*\^(\d{2})(\d{2})\d{3,}/;

function parseTrackData(raw) {
  const match = raw.match(TRACK1_REGEX);
  if (!match) return null;
  return {
    creditCardNumber: match[1],
    expYear: match[2], // YY — track data is YYMM order
    expMonth: match[3],
  };
}

function trackSwipe(analytics, eventName, data = {}) {
  logEvent(analytics, eventName, data);
  Sentry.addBreadcrumb({
    category: "card-swipe",
    message: eventName,
    level: "info",
    data,
  });
}

const getValidationSchema = (useSavedCard) => {
  if (useSavedCard) {
    return Yup.object().shape({
      promotionCode: Yup.string(),
    });
  }
  return Yup.object().shape({
    creditCardNumber: Yup.string()
      .min(14, "Credit card number must be at least 14 digits")
      .max(19, "Credit card number must be at most 19 digits")
      .required("Credit card number is required"),
    expMonth: Yup.string()
      .length(2, "Expiration month must be 2 digits")
      .required("Expiration month is required"),
    expYear: Yup.string()
      .length(2, "Expiration year must be 2 digits")
      .required("Expiration year is required"),
    promotionCode: Yup.string(),
  });
};

function StripeCardSection({ amount, isContract, recurringAmount, autopayInterval, onSuccess, onError, disabled }) {
  const stripe = useStripe();
  const elements = useElements();
  const { createStripePaymentIntent, createStripeSubscription } = useStore();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handlePay = async () => {
    if (!stripe || !elements || isLoading || disabled) return;
    setIsLoading(true);
    setErrorMessage(null);
    try {
      let clientSecret;
      let subscriptionId = null;

      if (isContract) {
        const result = await createStripeSubscription();
        clientSecret = result.clientSecret;
        subscriptionId = result.subscriptionId;
      } else {
        clientSecret = await createStripePaymentIntent(amount);
      }

      const cardElement = elements.getElement(CardElement);
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: cardElement },
      });
      if (error) {
        setErrorMessage(error.message);
        onError(error.message);
      } else if (paymentIntent && paymentIntent.status === "succeeded") {
        onSuccess(paymentIntent.id, subscriptionId);
      } else {
        const msg = "Payment could not be completed. Please try again.";
        setErrorMessage(msg);
        onError(msg);
      }
    } catch (e) {
      const msg = e?.message || "Something went wrong. Please try again.";
      setErrorMessage(msg);
      onError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      {isContract && recurringAmount > 0 && (
        <p className="text-muted mb-3" style={{ fontSize: "0.9rem" }}>
          Your card will be saved for automatic renewal of ${recurringAmount.toFixed(2)}/{autopayInterval || "month"}.
        </p>
      )}
      <div
        style={{
          border: "1px solid #ced4da",
          borderRadius: "0.375rem",
          padding: "12px 16px",
          backgroundColor: "#fff",
          boxShadow: "inset 0 1px 2px rgba(0,0,0,.075)",
        }}
      >
        <CardElement
          options={{
            disableLink: true,
            style: {
              base: {
                fontSize: "18px",
                color: "#424770",
                "::placeholder": { color: "#aab7c4" },
              },
              invalid: { color: "#9e2146" },
            },
          }}
        />
      </div>
      {errorMessage && (
        <div className="alert alert-danger mt-3" role="alert">
          {errorMessage}
        </div>
      )}
      <button
        type="button"
        className="btn btn-primary btn-large btn-lg w-auto ms-2 mt-4"
        data-testid="checkout-stripe-buy-btn"
        onClick={handlePay}
        disabled={isLoading || disabled || !stripe}
      >
        {isLoading ? (
          <span className="spinner-grow spinner-grow-sm" aria-hidden="true" />
        ) : (
          "Buy"
        )}
      </button>
    </div>
  );
}

export default function NewCheckoutModal() {
  const {
    showCheckoutModal,
    setShowCheckoutModal,
    selectedClient,
    selectedPopularItem,
    purchaseContract,
    purchaseItemService,
    setSelectedClient,
    setSelectedPopularItem,
    errorTimes,
    setErrorTimes,
    setCheckoutPayload,
    savedCard,
    hasSavedCard,
    loadingCard,
    getClientSavedCard,
    completeStripeCheckout,
  } = useStore();

  const {
    selectedSchedule,
    joinSchedule,
    setShowMapResourceModal,
    setShowEventNotificationModal,
    eventModalEnabled,
    setResource,
    pendingJoin,
    setPendingJoin,
    locationPreferences,
  } = useFlow();

  const navigate = useNavigate();
  const analytics = getAnalytics();

  const [cardSwipeData, setCardSwipeData] = useState("");
  const [showPromoCode, setShowPromoCode] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);
  const [requestGoing, setRequestGoing] = useState(0);
  const [checkoutData, setCheckoutData] = useState({
    creditCardNumber: "",
    expMonth: "",
    expYear: "",
    promotionCode: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [updatedPrice, setUpdatedPrice] = useState(null);
  const [updatedTax, setUpdatedTax] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("newCard"); // "savedCard" or "newCard"

  const manualRef = useRef();
  const ccRef = useRef();
  const formikRef = useRef();
  const submitMethodRef = useRef("manual");
  const swipeFailureReportedRef = useRef(false);
  const wrongFieldReportedRef = useRef(false);
  const visibleFieldSubmitFiredRef = useRef(false);

  useEffect(() => {
    if (showOverlay) return;
    manualRef.current.focus();
  }, [showOverlay]);

  // Parse card swipe data captured in the hidden field
  useEffect(() => {
    if (requestGoing >= 1) return;
    if (!cardSwipeData) return;

    const startsWithTrack = cardSwipeData.startsWith("%B");
    const hasCarets = cardSwipeData.includes("^");

    if (cardSwipeData.length === 1) {
      trackSwipe(analytics, "card_swipe_detected", { field: "hidden" });
    }

    const parsed = parseTrackData(cardSwipeData);
    if (parsed) {
      trackSwipe(analytics, "card_swipe_parsed", {
        success: true,
        rawLength: cardSwipeData.length,
        startsWithTrack,
        hasCarets,
      });
      submitMethodRef.current = "swipe";
      setRequestGoing(1);
      setCardSwipeData("");
      ccRef.current.blur();
      formikRef.current
        .setValues({
          creditCardNumber: parsed.creditCardNumber,
          expYear: parsed.expYear,
          expMonth: parsed.expMonth,
          promotionCode: formikRef.current.values.promotionCode,
        })
        .then(() => {
          formikRef.current.submitForm();
        });
    } else if (
      startsWithTrack &&
      cardSwipeData.length > 60 &&
      !swipeFailureReportedRef.current
    ) {
      swipeFailureReportedRef.current = true;
      trackSwipe(analytics, "card_swipe_parsed", {
        success: false,
        rawLength: cardSwipeData.length,
        startsWithTrack,
        hasCarets,
      });
      Sentry.captureMessage(
        "card swipe regex failed — track data in hidden field did not match",
        "warning"
      );
    }
  }, [cardSwipeData, requestGoing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset state on modal open/close
  useEffect(() => {
    if (!showCheckoutModal) {
      formikRef?.current?.resetForm();
      setCardSwipeData("");
      setShowPromoCode(false);
      setShowOverlay(true);
      setRequestGoing(0);
      submitMethodRef.current = "manual";
      swipeFailureReportedRef.current = false;
      wrongFieldReportedRef.current = false;
      visibleFieldSubmitFiredRef.current = false;
      setCheckoutData({
        creditCardNumber: "",
        expMonth: "",
        expYear: "",
        promotionCode: "",
      });
      setSelectedClient(null);
      setSelectedPopularItem(null);
      setPendingJoin(null);
      setUpdatedPrice(null);
      setUpdatedTax(null);
      setErrorTimes(0);
      setPaymentMethod("newCard");
    } else {
      trackSwipe(analytics, "checkout_modal_opened", {
        hasClient: !!selectedClient,
      });
      // From here on the item carries the whole line, not one unit — every
      // price on the screen and the amount tendered are line totals.
      //
      // The tax is recomputed from the rate rather than multiplied, because
      // Mindbody taxes the line and rounds once. Multiplying a rounded unit
      // tax gave 10 × $3 snacks at 8.25% as 32.50 where Mindbody wanted 32.48,
      // and a cart that is a cent out is a cart Mindbody rejects outright.
      // Only services and products, the two the cart path can send: a
      // contract's or a package's `taxRate` is derived from an already-rounded
      // figure, so multiplying its `taxValue` stays the better answer there.
      setSelectedPopularItem((prevItem) => {
        const quantity = prevItem?.quantity || 1;
        const perLineRate =
          (prevItem?.type === "service" || prevItem?.type === "product") &&
          typeof prevItem?.taxRate === "number";

        return {
          ...prevItem,
          price: prevItem?.price * quantity,
          oldPrice: prevItem?.oldPrice * quantity,
          taxValue: perLineRate
            ? lineTaxValue(prevItem.price, prevItem.taxRate, quantity)
            : (Number(prevItem?.taxValue) || 0) * quantity,
        };
      });
    }
  }, [showCheckoutModal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch saved card when client is known — split from modal reset to avoid re-triggering focus
  useEffect(() => {
    if (!showCheckoutModal) return;
    if (selectedClient?.id) {
      getClientSavedCard(selectedClient.id);
    } else {
      setPaymentMethod("newCard");
    }
  }, [showCheckoutModal, selectedClient?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Set saved card as default when available
  useEffect(() => {
    if (hasSavedCard && savedCard && !savedCard.isExpired && selectedClient) {
      setPaymentMethod("savedCard");
    } else if (!hasSavedCard && !loadingCard && selectedClient) {
      setPaymentMethod("newCard");
    }
  }, [hasSavedCard, savedCard, loadingCard, selectedClient]);

  const handleClose = () => setShowCheckoutModal(false);

  // Fires after Bootstrap modal CSS transition completes — safe point to acquire focus
  const handleModalEntered = () => {
    ccRef.current?.focus();
    const focused = document.activeElement === ccRef.current;
    trackSwipe(analytics, "checkout_focus_status", {
      hiddenFieldHasFocus: focused,
    });
    if (!focused) {
      Sentry.captureMessage(
        "checkout hidden field failed to acquire focus on modal open",
        "warning"
      );
    }
  };

  // Called when track data lands in the visible card number field instead of the hidden one.
  // Recovers the swipe by parsing from the raw value and auto-submitting.
  const handleTrackDataInVisibleField = (raw) => {
    if (visibleFieldSubmitFiredRef.current || requestGoing >= 1) return;

    const startsWithTrack = raw.startsWith("%B");
    const hasCarets = raw.includes("^");

    if (!wrongFieldReportedRef.current) {
      wrongFieldReportedRef.current = true;
      trackSwipe(analytics, "card_swipe_detected", { field: "visible" });
      trackSwipe(analytics, "card_swipe_wrong_field", {
        overlayDismissed: !showOverlay,
        rawLength: raw.length,
        startsWithTrack,
        hasCarets,
      });
      Sentry.captureMessage(
        "card swipe data landed in visible creditCardNumber field",
        "warning"
      );
    }

    const parsed = parseTrackData(raw);
    if (parsed) {
      visibleFieldSubmitFiredRef.current = true;
      trackSwipe(analytics, "card_swipe_parsed", {
        success: true,
        rawLength: raw.length,
        startsWithTrack,
        hasCarets,
        recoveredFromVisibleField: true,
      });
      submitMethodRef.current = "swipe";
      setRequestGoing(1);
      formikRef.current
        .setValues({
          creditCardNumber: parsed.creditCardNumber,
          expYear: parsed.expYear,
          expMonth: parsed.expMonth,
          promotionCode: formikRef.current.values.promotionCode,
        })
        .then(() => {
          formikRef.current.submitForm();
        });
    } else if (raw.length > 60 && !swipeFailureReportedRef.current) {
      swipeFailureReportedRef.current = true;
      trackSwipe(analytics, "card_swipe_parsed", {
        success: false,
        rawLength: raw.length,
        startsWithTrack,
        hasCarets,
        recoveredFromVisibleField: false,
      });
    }
  };

  const handleStripeSuccess = async (paymentIntentId, subscriptionId = null) => {
    try {
      setRequestGoing(2);
      await completeStripeCheckout(paymentIntentId, subscriptionId);
      if (pendingJoin) {
        await joinSchedule(pendingJoin.scheduleId, pendingJoin.client);
        setResource({
          name: selectedSchedule.resourceName,
          id: selectedSchedule.resourceId,
        });
        navigate(-1);
        if (
          eventModalEnabled &&
          !document.cookie.includes(`votedClientId_${pendingJoin.client.id}`)
        )
          setShowEventNotificationModal(true);
        else setShowMapResourceModal(true);
        setPendingJoin(null);
      } else toast.success("Purchase completed successfully");
      setSelectedClient(null);
      setSelectedPopularItem(null);
      setErrorTimes(0);
      setRequestGoing(0);
      handleClose();
    } catch (e) {
      setErrorTimes(errorTimes + 1);
      if (e?.code === "ClientSuspended") {
        toast.error(
          e?.message ||
            "Your account is on hold. Please see a staff member to check you in."
        );
      } else toast.error(`Something went wrong! ${e?.message}`);
      setRequestGoing(0);
    }
  };

  const handleStripeError = () => {
    setErrorTimes(errorTimes + 1);
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!selectedPopularItem) return;
    if (requestGoing >= 2) return;
    try {
      setRequestGoing(2);

      const method =
        paymentMethod === "savedCard" ? "savedCard" : submitMethodRef.current;
      trackSwipe(analytics, "checkout_submitted", { method });

      const purchaseData = {
        ...values,
        useSavedCard:
          paymentMethod === "savedCard" &&
          hasSavedCard &&
          savedCard &&
          !savedCard.isExpired,
      };

      setCheckoutPayload(purchaseData);
      const purchaseResp =
        selectedPopularItem?.type === "contract"
          ? await purchaseContract(purchaseData)
          : await purchaseItemService(purchaseData);

      // Idempotency (FLO-159): the backend blocks a duplicate submit (double-tap /
      // retry) and returns an explicit marker instead of charging again. Treat it
      // as a non-error — never surface it as a payment failure.
      const isDuplicate = purchaseResp?.data?.duplicate === true;
      if (isDuplicate)
        toast.info(
          "This purchase was already submitted a moment ago — you were not charged again."
        );

      if (pendingJoin) {
        await joinSchedule(pendingJoin.scheduleId, pendingJoin.client);
        setResource({
          name: selectedSchedule.resourceName,
          id: selectedSchedule.resourceId,
        });
        navigate(-1);
        if (
          eventModalEnabled &&
          !document.cookie.includes(`votedClientId_${pendingJoin.client.id}`)
        )
          setShowEventNotificationModal(true);
        else setShowMapResourceModal(true);
        setPendingJoin(null);
      } else if (!isDuplicate) toast.success("Purchase completed successfully");
      setSelectedClient(null);
      setSelectedPopularItem(null);
      setSubmitting(false);
      setErrorTimes(0);
      setCheckoutPayload(null);
      clearHubSpotState();
      handleClose();
    } catch (e) {
      setErrorTimes(errorTimes + 1);
      if (
        e?.message?.indexOf("CatalogItemDoesNotMeetIntroOfferRestrictions") > -1
      ) {
        toast.error(
          "You are no longer eligible for this offer. Please try another service."
        );
      } else if (e?.code === "ClientSuspended") {
        toast.error(
          e?.message ||
            "Your account is on hold. Please see a staff member to check you in."
        );
      } else toast.error(`Something went wrong! ${e?.message}`);

      setRequestGoing(0);
      setSubmitting(false);
    }
  };

  const validatePromoCode = async () => {
    try {
      if (formikRef.current.values.promotionCode === "") {
        setUpdatedPrice(null);
        setUpdatedTax(null);
        setShowPromoCode(false);
        return;
      }
      setIsLoading(true);
      const payload = {
        creditCardNumber: "4242424242424242",
        expMonth: "01",
        expYear: "30",
        promotionCode: formikRef.current.values.promotionCode,
        devMode: true,
      };

      let response;

      if (selectedPopularItem?.type === "contract")
        response = await purchaseContract(payload);
      else response = await purchaseItemService(payload);

      let updatedPrice;
      let updatedTax;

      if (response.data?.ShoppingCart?.GrandTotal !== undefined)
        updatedPrice = response.data?.ShoppingCart?.GrandTotal;
      else if (response.data.Totals?.Total !== undefined)
        updatedPrice = response.data.Totals?.Total;

      if (response.data?.ShoppingCart?.TaxTotal !== undefined)
        updatedTax = response.data?.ShoppingCart?.TaxTotal;
      else if (response.data.Totals?.Tax !== undefined)
        updatedTax = response.data.Totals?.Tax;

      if (updatedPrice > selectedPopularItem?.price) {
        setUpdatedPrice(null);
        setUpdatedTax(null);
      } else {
        setUpdatedPrice(updatedPrice);
        setUpdatedTax(updatedTax);
      }

      setShowPromoCode(false);
      setIsLoading(false);
      ccRef.current.focus();
    } catch (error) {
      toast.error(`Invalid promo code!`);
      setIsLoading(false);
    }
  };

  return (
    <Modal
      show={showCheckoutModal}
      onHide={handleClose}
      centered
      size="xl"
      onEntered={handleModalEntered}
    >
      <Modal.Body className="p-5">
        <Formik
          initialValues={checkoutData}
          validationSchema={
            selectedPopularItem?.price === 0
              ? null
              : getValidationSchema(
                  paymentMethod === "savedCard" &&
                    hasSavedCard &&
                    savedCard &&
                    !savedCard.isExpired
                )
          }
          onSubmit={handleSubmit}
          innerRef={formikRef}
          enableReinitialize
        >
          {({ isSubmitting, values }) => (
            <Form id="checkoutForm" acceptCharset="utf-8" autoComplete="off">
              <Field
                id="testnumber"
                type="text"
                style={{ opacity: 0, position: "absolute", zIndex: -1 }}
                value={cardSwipeData}
                onChange={(event) => setCardSwipeData(event.target.value)}
                autoComplete="off"
                innerRef={ccRef}
              />
              <div className="row">
                <div
                  className="col-4 rounded-4 p-4"
                  style={{ backgroundColor: "#eee" }}
                >
                  {selectedPopularItem && (
                    <div className="product-details pb-4 d-block">
                      <span className="product-name">
                        {selectedPopularItem.quantity || 1}x{" "}
                        {itemDisplayName(selectedPopularItem)}
                      </span>
                      <span
                        className="product-price float-end"
                        style={{ minWidth: 120, textAlign: "right" }}
                      >
                        $
                        {updatedPrice !== null
                          ? updatedPrice.toFixed(2)
                          : selectedPopularItem.price.toFixed(2)}
                      </span>
                      {selectedPopularItem?.taxValue > 0 && (
                        <p
                          className="product-price float-end"
                          style={{ opacity: 0.6, fontSize: 12 }}
                        >
                          plus $
                          {updatedTax !== null
                            ? updatedTax.toFixed(2)
                            : selectedPopularItem?.taxValue.toFixed(2)}{" "}
                          of service fee
                        </p>
                      )}
                    </div>
                  )}

                  {selectedClient && (
                    <div id="user-details" className="py-4 mb-4 d-block">
                      <strong>Name: </strong>
                      <span className="full_name">{selectedClient?.name}</span>
                      <br />
                      <strong>Phone: </strong>
                      <span className="phone">{selectedClient?.phone}</span>
                    </div>
                  )}
                  {!showPromoCode && (
                    <span
                      className="pointer"
                      onClick={() => {
                        setShowPromoCode(true);
                        setUpdatedPrice(null);
                        setUpdatedTax(null);
                      }}
                    >
                      {values.promotionCode ? (
                        <i className="text-success">
                          Code: {values.promotionCode}
                        </i>
                      ) : (
                        <i>Enter promo code</i>
                      )}
                    </span>
                  )}
                  {showPromoCode && (
                    <>
                      <Row className="mt-3">
                        <Col xs={8}>
                          <Field
                            className="datasetfield w-100 form-control"
                            type=""
                            name="promotionCode"
                            id="promotionCode"
                            placeholder="Promo Code"
                            autoComplete="off"
                            autoFocus="autofocus"
                            disabled={
                              isSubmitting || requestGoing > 0 || isLoading
                            }
                            onKeyPress={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                validatePromoCode();
                              }
                            }}
                          />
                        </Col>
                        <Col>
                          <Button
                            onClick={validatePromoCode}
                            disabled={
                              isSubmitting || requestGoing > 0 || isLoading
                            }
                          >
                            {isLoading ? (
                              <span
                                className="spinner-grow spinner-grow-sm"
                                aria-hidden="true"
                              />
                            ) : (
                              "OK"
                            )}
                          </Button>
                        </Col>
                      </Row>
                    </>
                  )}
                </div>
                {selectedPopularItem?.price > 0 && (
                  <div className="col-8 py-5 my-5">
                    {locationPreferences?.stripeCheckoutEnabled ? (
                      /* Stripe inline card form — handles both products/services and contracts */
                      <Elements stripe={stripePromise}>
                        <StripeCardSection
                          amount={itemCheckoutAmount(selectedPopularItem)}
                          isContract={selectedPopularItem?.type === "contract"}
                          recurringAmount={selectedPopularItem?.recurringAmount}
                          autopayInterval={selectedPopularItem?.autopayInterval}
                          onSuccess={handleStripeSuccess}
                          onError={handleStripeError}
                          disabled={requestGoing > 0}
                        />
                      </Elements>
                    ) : (
                      /* Legacy MBO card flow */
                      <>
                        {/* Loading state while fetching saved card */}
                        {selectedClient && loadingCard ? (
                          <div className="text-center py-5">
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            />
                            <span className="text-muted">
                              Loading payment options...
                            </span>
                          </div>
                        ) : (
                          <>
                            {/* Payment method selection - only show if has saved card */}
                            {selectedClient &&
                              hasSavedCard &&
                              savedCard &&
                              !savedCard.isExpired && (
                                <div className="mb-4">
                                  <label className="form-label fw-bold">
                                    Payment Method
                                  </label>
                                  <div>
                                    <div className="form-check mb-2">
                                      <input
                                        className="form-check-input"
                                        type="radio"
                                        name="paymentMethod"
                                        id="savedCard"
                                        checked={paymentMethod === "savedCard"}
                                        onChange={() => {
                                          setPaymentMethod("savedCard");
                                        }}
                                        disabled={isSubmitting || requestGoing > 0}
                                      />
                                      <label
                                        className="form-check-label"
                                        htmlFor="savedCard"
                                      >
                                        Use saved card ••••{savedCard.lastFour} (
                                        {savedCard.cardType} - Exp:{" "}
                                        {savedCard.expMonth}/{savedCard.expYear})
                                      </label>
                                    </div>
                                    <div className="form-check">
                                      <input
                                        className="form-check-input"
                                        type="radio"
                                        name="paymentMethod"
                                        id="newCard"
                                        checked={paymentMethod === "newCard"}
                                        onChange={() => {
                                          setPaymentMethod("newCard");
                                        }}
                                        disabled={isSubmitting || requestGoing > 0}
                                      />
                                      <label
                                        className="form-check-label"
                                        htmlFor="newCard"
                                      >
                                        Use new card
                                      </label>
                                    </div>
                                  </div>
                                </div>
                              )}

                            {/* Card form - only show if new card selected, no saved card, or no client */}
                            {(!selectedClient ||
                              !hasSavedCard ||
                              paymentMethod === "newCard" ||
                              (savedCard && savedCard.isExpired)) && (
                              <div className="row position-relative">
                                {showOverlay && (
                                  <div
                                    className="checkoutOverlay pointer"
                                    data-testid="checkout-swipe-overlay"
                                    onClick={() => setShowOverlay(false)}
                                  >
                                    <h1
                                      className="fw-bold mt-1"
                                      style={{ fontSize: "3.5rem" }}
                                    >
                                      Swipe your card
                                    </h1>
                                    <div className="text-center mt-5">
                                      <button
                                        className="btn btn-lg text-decoration-underline text-secondary"
                                        data-testid="checkout-manual-card-btn"
                                        onClick={() => setShowOverlay(false)}
                                        disabled={isSubmitting || requestGoing > 0}
                                      >
                                        {isSubmitting || requestGoing > 0
                                          ? "processing..."
                                          : "manually type card"}
                                      </button>
                                    </div>
                                  </div>
                                )}
                                <div className="col-md-12 py-2">
                                  <Field name="creditCardNumber">
                                    {({ field }) => (
                                      <input
                                        {...field}
                                        className="datasetfield w-100 form-control"
                                        type="text"
                                        id="creditCardNumber"
                                        placeholder="Credit card number"
                                        autoComplete="off"
                                        disabled={isSubmitting || requestGoing > 0}
                                        ref={manualRef}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          if (
                                            val.startsWith("%B") ||
                                            (val.startsWith("%") &&
                                              val.includes("^"))
                                          ) {
                                            handleTrackDataInVisibleField(val);
                                            return;
                                          }
                                          field.onChange(e);
                                        }}
                                      />
                                    )}
                                  </Field>
                                  <ErrorMessage
                                    className="invalid-feedback d-block"
                                    name="creditCardNumber"
                                    component="div"
                                  />
                                </div>

                                <div className="col-md-12 py-2">
                                  <Field
                                    className="datasetfield form-control"
                                    name="expMonth"
                                    id="expMonth"
                                    placeholder="03"
                                    maxLength="2"
                                    size="2"
                                    autoComplete="off"
                                    style={{
                                      display: "inline-block",
                                      width: "auto",
                                    }}
                                    disabled={isSubmitting || requestGoing > 0}
                                  />
                                  <span
                                    style={{
                                      display: "inline-block",
                                      padding: "0 5px",
                                    }}
                                  >
                                    /
                                  </span>
                                  <Field
                                    className="datasetfield form-control"
                                    name="expYear"
                                    id="expYear"
                                    placeholder="25"
                                    maxLength="2"
                                    size="2"
                                    autoComplete="off"
                                    style={{
                                      display: "inline-block",
                                      width: "auto",
                                    }}
                                    disabled={isSubmitting || requestGoing > 0}
                                  />
                                  <ErrorMessage
                                    className="invalid-feedback d-block"
                                    name="expMonth"
                                    component="div"
                                  />
                                  <ErrorMessage
                                    className="invalid-feedback d-block"
                                    name="expYear"
                                    component="div"
                                  />
                                </div>
                                <button
                                  id="pay_amount"
                                  type="submit"
                                  className="btn btn-primary btn-large btn-lg w-auto ms-2 mt-2"
                                  data-testid="checkout-buy-btn"
                                  disabled={isSubmitting || requestGoing > 0}
                                >
                                  {isSubmitting || requestGoing > 0 ? (
                                    <span
                                      className="spinner-grow spinner-grow-sm"
                                      aria-hidden="true"
                                    />
                                  ) : (
                                    "Buy"
                                  )}
                                </button>
                              </div>
                            )}

                            {/* Buy button when using saved card */}
                            {selectedClient &&
                              hasSavedCard &&
                              savedCard &&
                              !savedCard.isExpired &&
                              paymentMethod === "savedCard" && (
                                <div className="mt-4">
                                  <button
                                    id="pay_amount"
                                    type="submit"
                                    className="btn btn-primary btn-large btn-lg w-100"
                                    data-testid="checkout-buy-btn"
                                    disabled={isSubmitting || requestGoing > 0}
                                  >
                                    {isSubmitting || requestGoing > 0 ? (
                                      <span
                                        className="spinner-grow spinner-grow-sm"
                                        aria-hidden="true"
                                      />
                                    ) : (
                                      "Buy"
                                    )}
                                  </button>
                                </div>
                              )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
                {selectedPopularItem?.price === 0 && (
                  <div className="col-8 py-5 my-5 text-center">
                    <button
                      id="pay_amount"
                      type="submit"
                      className="btn btn-primary btn-large btn-lg"
                      data-testid="checkout-buy-btn"
                      disabled={isSubmitting || requestGoing > 0}
                      style={{
                        width: 180,
                        height: 60,
                      }}
                    >
                      {isSubmitting || requestGoing > 0 ? (
                        <span
                          className="spinner-grow spinner-grow-sm"
                          aria-hidden="true"
                        />
                      ) : (
                        "Buy"
                      )}
                    </button>
                  </div>
                )}
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
      <Modal.Footer
        className="border-0 p-4"
        style={{ marginTop: "-80px", zIndex: "100" }}
      >
        <a
          className="pointer"
          data-testid="checkout-cancel-btn"
          onClick={handleClose}
          style={{
            color: "inherit",
            textDecoration: "none",
          }}
        >
          Cancel
        </a>
      </Modal.Footer>
    </Modal>
  );
}
